import { useRef } from "react";
import { TraceabilityManufacturedViewModel } from "../../viewmodels/traceability/TraceabilityManufacturedViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";

import { withRouter } from "../../shared/utility";

import SlideOver from "../../components/slideOver/SlideOver";

import ReceivingItemInformation from "../../components/forms/production/ReceivingItemInformation";
import TraceLotNumber from "../../components/forms/traceability/TraceLotNumber";
import TraceSupplierItem from "../../components/forms/traceability/TraceSupplierItem";
import PluSelector from "../../components/forms/traceability/PluSelector";
import EmptyModal from "../../components/modals/EmptyModal";
import { Skeleton } from "primereact/skeleton";

function getPluOptions(manufacturedByLotResult) {
  if (!manufacturedByLotResult) return [];
  if (Array.isArray(manufacturedByLotResult)) return manufacturedByLotResult;
  return manufacturedByLotResult.manufactured_products ?? [];
}

function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function TraceabilityManufactured(props) {
  const { logout } = props;
  const {
    loading,
    packagingByLotResult,
    manufacturedTraceResult,
    manufacturedLotNumber,
    slideOver,
    setSlideOver,
    onTracePackagingByLot,
    onTracePackagingByLotAndPlu,
    onSearchLotNumbers,
    reassignPackagingsById,
    selectedProductionId,
    reportLoading,
    onPrintTraceManufacturedByLotAndPluReport,
    emptyModal,
    setEmptyModelOpen,
  } = TraceabilityManufacturedViewModel(logout);

  const searchRef = useRef();

  const handleSearch = () => {
    onTracePackagingByLot(searchRef.current?.value);
  };

  const openReceivingItemInformation = function (assemblyItem) {
    setSlideOver({
      childComponent: (
        <ReceivingItemInformation
          receivingItem={assemblyItem}
          setOpen={setSlideOver}
        />
      ),
      open: true,
    });
  };

  const pluOptions = getPluOptions(packagingByLotResult);

  const renderTraceResults = function () {
    if (manufacturedTraceResult) {
      if (
        manufacturedTraceResult.receiving_items != null ||
        manufacturedTraceResult.manufactured_products != null ||
        manufacturedTraceResult.packaged_products != null
      ) {
        return <TraceSupplierItem product={manufacturedTraceResult} />;
      }
      return (
        <TraceLotNumber
          product={manufacturedTraceResult}
          openReceivingItemInformation={openReceivingItemInformation}
          onSearchLotNumbers={onSearchLotNumbers}
          reassignPackagingsById={reassignPackagingsById}
          onRefreshTrace={() => {
            if (selectedProductionId == null || selectedProductionId === "") {
              return Promise.resolve();
            }
            return onTracePackagingByLotAndPlu(selectedProductionId);
          }}
        />
      );
    }
    if (pluOptions.length > 0) {
      return (
        <PluSelector
          pluOptions={pluOptions}
          lotNumber={manufacturedLotNumber}
          onSelectPlu={(productionId) =>
            onTracePackagingByLotAndPlu(productionId)
          }
        />
      );
    }
    if (packagingByLotResult && pluOptions.length === 0) {
      return (
        <div className="mt-4 p-4 card-custom text-gray-600">
          No trace found.
        </div>
      );
    }
    return null;
  };

  const hasManufacturedResults =
    manufacturedTraceResult ||
    pluOptions.length > 0 ||
    (packagingByLotResult && pluOptions.length === 0);
  const canPrintTraceReport =
    selectedProductionId != null &&
    selectedProductionId !== "" &&
    manufacturedTraceResult != null;
  const inventoryRows = Array.isArray(manufacturedTraceResult?.inventory)
    ? manufacturedTraceResult.inventory
    : [];
  const inventoryKpis = inventoryRows.reduce(
    (acc, row) => {
      acc.totalInStock += toNumber(row?.inventory_in_stock);
      acc.totalSold += toNumber(row?.inventory_sold);
      acc.totalPackaged += toNumber(row?.total_packaged);
      return acc;
    },
    { totalInStock: 0, totalSold: 0, totalPackaged: 0 },
  );
  const shippingRows = Array.isArray(manufacturedTraceResult?.shippings)
    ? manufacturedTraceResult.shippings
    : [];
  const shippingKpis = shippingRows.reduce(
    (acc, row) => {
      acc.shipments += 1;
      acc.totalSold += toNumber(row?.total_sold);
      return acc;
    },
    { shipments: 0, totalSold: 0 },
  );
  const showKpiStrip =
    manufacturedTraceResult &&
    (inventoryRows.length > 0 || shippingRows.length > 0);

  return (
    <div>
      <div className="mt-2 lg:px-8">
        <div className="card-custom px-4 py-5">
          <div className="sm:flex sm:items-start sm:justify-between">
            <div>
              <h1 className="text-lg font-semibold leading-6 text-gray-900">
                Trace Manufactured Products
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Search by lot number, then drill down by PLU to inspect full
                traceability.
              </p>
            </div>
            {canPrintTraceReport ? (
              <div className="mt-4 sm:mt-0 sm:ml-4">
                <button
                  type="button"
                  onClick={onPrintTraceManufacturedByLotAndPluReport}
                  disabled={reportLoading || loading}
                  className="inline-flex items-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-60"
                >
                  {reportLoading ? "Generating..." : "Print"}
                </button>
              </div>
            ) : null}
          </div>
          <div className="mt-4 flex rounded-md">
            <div className="relative flex items-stretch flex-grow focus-within:z-10">
              <input
                type="search"
                name="searchProduct"
                id="searchProduct"
                className="block w-full rounded-l-md border border-gray-300 pl-3 pr-3 text-sm focus:border-main-purple focus:ring-main-purple"
                placeholder="Search by lot number"
                ref={searchRef}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearch();
                }}
              />
            </div>
            <button
              type="button"
              className="-ml-px relative inline-flex items-center gap-1 rounded-r-md border border-gray-300 bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-1"
              onClick={() => handleSearch()}
            >
              <MagnifyingGlassIcon
                className="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
              Search
            </button>
          </div>
        </div>
        {showKpiStrip ? (
          <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                SKUs
              </p>
              <p className="mt-1 text-xl font-semibold text-gray-900">
                {inventoryRows.length}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Total Packaged
              </p>
              <p className="mt-1 text-xl font-semibold text-gray-900">
                {inventoryKpis.totalPackaged}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                In Stock
              </p>
              <p className="mt-1 text-xl font-semibold text-gray-900">
                {inventoryKpis.totalInStock}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Sold
              </p>
              <p className="mt-1 text-xl font-semibold text-gray-900">
                {inventoryKpis.totalSold}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
              <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                Shipments
              </p>
              <p className="mt-1 text-xl font-semibold text-gray-900">
                {shippingKpis.shipments}
              </p>
              {/* <p className="text-xs text-gray-500">
                Total shipped: {shippingKpis.totalSold}
              </p> */}
            </div>
          </div>
        ) : null}
        {loading ? (
          <div className="py-4 space-y-4">
            <Skeleton
              className="w-full"
              height="5rem"
              borderRadius="16px"
            ></Skeleton>
            <Skeleton
              className="w-full"
              height="5rem"
              borderRadius="16px"
            ></Skeleton>
            <Skeleton
              className="w-full"
              height="5rem"
              borderRadius="16px"
            ></Skeleton>
            <Skeleton
              className="w-full"
              height="5rem"
              borderRadius="16px"
            ></Skeleton>
          </div>
        ) : hasManufacturedResults ? (
          renderTraceResults()
        ) : null}
      </div>

      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
      <SlideOver
        childComponent={slideOver.childComponent}
        slideOver={slideOver}
        customClass="max-w-3xl"
        setSlideOver={setSlideOver}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {};
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(TraceabilityManufactured),
);
